import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  TagsInput,
  TagsInputValue,
} from "@arcadeai/design-system/components/ui/molecules/tags-input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { AlertTriangle } from "lucide-react";
import type { Control } from "react-hook-form";
import { isInternalHeader } from "../../constants/internal-headers";
import type { McpServerCreateRequest } from "../../types";
import { HEADER_NAME_TOKEN_REGEX } from "../../types";

type AllowedPassthroughHeadersFieldProps = {
  control: Control<McpServerCreateRequest>;
};

export const AllowedPassthroughHeadersField = ({
  control,
}: AllowedPassthroughHeadersFieldProps) => {
  return (
    <FormField
      control={control}
      name="allowed_passthrough_headers"
      render={({ field }) => {
        const headers: string[] = field.value ?? [];
        return (
          <FormItem>
            <FormLabel>Passthrough headers (optional)</FormLabel>
            <TooltipProvider>
              <FormControl>
                <TagsInput
                  isDuplicate={(a, b) =>
                    a.toLowerCase() === b.toLowerCase() &&
                    "Header already added"
                  }
                  onChange={field.onChange}
                  placeholder="Add header name…"
                  renderValue={(name, remove) => (
                    <span className="flex items-center gap-0.5">
                      <TagsInputValue onRemove={remove} value={name} />
                      {isInternalHeader(name) && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <span className="inline-flex items-center text-warning" />
                            }
                          >
                            <AlertTriangle className="size-3.5" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs" side="top">
                            This header is normally set by the gateway. Allowing
                            it here will overwrite the gateway's outbound value
                            and may break the connection to the server.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  )}
                  validateValue={(trimmed) => {
                    if (!HEADER_NAME_TOKEN_REGEX.test(trimmed)) {
                      return {
                        error:
                          "Invalid header name. Use visible ASCII characters only (no spaces or special delimiters)",
                      };
                    }
                    return {};
                  }}
                  values={headers}
                />
              </FormControl>
            </TooltipProvider>
            <FormDescription>
              Inbound HTTP header names to forward to the worker on every
              request. Press Enter or comma to add. Duplicate values are
              ignored.
            </FormDescription>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
